import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  Link,
  TextField,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as ApprovedIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Timeline as AttendanceIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, ROLES } from "../../constants";
import { leavesAPI } from "../../services/api/leaves.api";
import { documentsAPI } from "../../services/api/documents.api";
import useNotification from "../../hooks/common/useNotification";

const LeaveDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const { hasRole } = useAuth();
  const { showSuccess, showError } = useNotification();

  // State management
  const [leave, setLeave] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  const [resubmitComment, setResubmitComment] = useState("");
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Check if this is an approval route
  const isApprovalRoute = location.pathname.includes("/approvals/");
  const isAdmin = hasRole(ROLES.ADMIN);
  const isHR = hasRole(ROLES.HR_MANAGER);

  useEffect(() => {
    if (id) {
      fetchLeaveDetails();
      fetchAttendanceRecords();
    }
  }, [id]);

  const fetchLeaveDetails = async () => {
    try {
      setLoading(true);
      console.log("Fetching leave details for ID:", id);

      const response = await leavesAPI.getById(id);
      console.log("Leave details response:", response);

      if (response && response.success) {
        setLeave(response.data);
        // If leave has attendanceRecords in the response, use them
        if (response.data.attendanceRecords) {
          setAttendanceRecords(response.data.attendanceRecords);
        }
      } else {
        throw new Error(response?.message || "Failed to fetch leave details");
      }
    } catch (error) {
      console.error("Error fetching leave details:", error);
      showError("Failed to load leave details");
      // Navigate back if leave not found
      setTimeout(() => navigate(-1), 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceRecords = async () => {
    if (!id) return;
    
    try {
      setLoadingAttendance(true);
      const response = await leavesAPI.getAttendanceRecords(id);
      
      if (response && response.success) {
        setAttendanceRecords(response.data.attendanceRecords || []);
      }
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      // Don't show error if endpoint doesn't exist or no records found
      setAttendanceRecords([]);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleEdit = () => {
    navigate(`${ROUTES.LEAVES}/${id}/edit`);
  };

  const handleCancel = async () => {
    try {
      setActionLoading(true);
      const response = await leavesAPI.cancel(id, {
        comment: "Leave cancelled by employee",
      });

      if (response && response.success) {
        showSuccess("Leave application cancelled successfully");
        await fetchLeaveDetails(); // Refresh data
      } else {
        throw new Error(response?.message || "Failed to cancel leave");
      }
    } catch (error) {
      console.error("Error cancelling leave:", error);
      showError(error.message || "Failed to cancel leave application");
    } finally {
      setActionLoading(false);
      setCancelDialogOpen(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      const response = await leavesAPI.delete(id);

      if (response && response.success) {
        showSuccess("Leave application deleted successfully");
        navigate(ROUTES.LEAVES);
      } else {
        throw new Error(response?.message || "Failed to delete leave");
      }
    } catch (error) {
      console.error("Error deleting leave:", error);
      showError(error.message || "Failed to delete leave application");
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleResubmit = async () => {
    try {
      setActionLoading(true);
      console.log("Resubmitting leave application:", id);

      // Include all required fields from the original leave application
      const updateData = {
        // Keep all original leave data
        type_id: leave.type_id,
        starting_date: leave.starting_date,
        end_date: leave.end_date,
        // Update status and comment
        approval_status: "pending",
        comment: resubmitComment || leave.comment,
        // Add resubmitted timestamp if needed by backend
        resubmitted_at: new Date().toISOString(),
        // Include any other fields that might be required
        ...(leave.attachment_id && { attachment_id: leave.attachment_id }),
      };

      console.log("Resubmit data:", updateData);

      const response = await leavesAPI.resubmit(id, updateData);

      if (response && response.success) {
        showSuccess(
          "Leave application resubmitted successfully and is now pending approval"
        );
        await fetchLeaveDetails(); // Refresh data
      } else {
        throw new Error(response?.message || "Failed to resubmit leave");
      }
    } catch (error) {
      console.error("Error resubmitting leave:", error);

      // Handle validation errors specifically
      if (
        error.response?.status === 400 &&
        error.response?.data?.validationErrors
      ) {
        const validationErrors = error.response.data.validationErrors;
        const errorMessage = validationErrors
          .map((err) => err.message)
          .join(", ");
        showError(`Validation failed: ${errorMessage}`);
      } else {
        showError(error.message || "Failed to resubmit leave application");
      }
    } finally {
      setActionLoading(false);
      setResubmitDialogOpen(false);
      setResubmitComment("");
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!attachment || !attachment.id) {
      showError("No attachment available for download");
      return;
    }

    try {
      const blob = await documentsAPI.download(attachment.id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.name || "document";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showSuccess("Document downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      if (error.message.includes("404")) {
        showError("Document not found or no longer available");
      } else {
        showError("Failed to download document");
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "approved by hr":
      case "approved by supervisor":
        return "info";
      case "rejected":
        return "error";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return <ApprovedIcon />;
      case "pending":
      case "approved by hr":
      case "approved by supervisor":
        return <PendingIcon />;
      case "rejected":
        return <RejectedIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const formatStatus = (status) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = Math.abs(end - start);
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1;
  };

  const canEditLeave = () => {
    if (!leave) return false;
    const isOwner = leave.user_id === user?.id;
    const editableStatuses = ["draft", "rejected"];
    return isOwner && editableStatuses.includes(leave.approval_status);
  };

  const canCancelLeave = () => {
    if (!leave) return false;
    const isOwner = leave.user_id === user?.id;
    const cancellableStatuses = [
      "pending",
      "approved by supervisor",
      "approved by hr",
    ];
    return isOwner && cancellableStatuses.includes(leave.approval_status);
  };

  const canDeleteLeave = () => {
    if (!leave) return false;
    const isOwner = leave.user_id === user?.id;
    const isDraft = leave.approval_status === "draft";
    return isOwner && isDraft;
  };

  const canResubmitLeave = () => {
    if (!leave) return false;
    const isOwner = leave.user_id === user?.id;
    const isRejected = leave.approval_status === "rejected";
    return isOwner && isRejected;
  };

  const canApproveLeave = () => {
    if (!leave) return false;

    // Type-safe same-user check (API may return number, auth may have string)
    const isSameUser = (a, b) => a != null && b != null && (Number(a) === Number(b) || String(a) === String(b));
    const isAssignedApprover = isSameUser(leave.approver_id, user?.id);

    // Supervisor who already approved must not see Approve again
    if (leave.approval_status === "approved by supervisor" && isSameUser(leave.approver_id, user?.id)) {
      return false;
    }

    // Supervisor can approve pending applications
    if (leave.approval_status === "pending" && isAssignedApprover) {
      return true;
    }

    // Admin can approve supervisor-approved applications (if they didn't approve it)
    if (isAdmin && leave.approval_status === "approved by supervisor") {
      return true;
    }

    return false;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!leave) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Leave application not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              Leave Application Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Application #{leave.id}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint} size="small">
              <PrintIcon />
            </IconButton>
          </Tooltip>

          {canEditLeave() && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              size="small"
            >
              {leave.approval_status === "rejected"
                ? "Edit & Resubmit"
                : "Edit"}
            </Button>
          )}

          {canResubmitLeave() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => setResubmitDialogOpen(true)}
              size="small"
            >
              Resubmit
            </Button>
          )}

          {canCancelLeave() && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              size="small"
            >
              Cancel
            </Button>
          )}

          {canDeleteLeave() && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              size="small"
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {/* Status Alert */}
      <Alert
        severity={getStatusColor(leave.approval_status)}
        icon={getStatusIcon(leave.approval_status)}
        sx={{ mb: 3 }}
        action={
          canResubmitLeave() ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => setResubmitDialogOpen(true)}
              startIcon={<RefreshIcon />}
            >
              Resubmit
            </Button>
          ) : null
        }
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
          Status: {formatStatus(leave.approval_status)}
        </Typography>
        {leave.comment && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Comment: {leave.comment}
          </Typography>
        )}
        {canResubmitLeave() && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
            You can resubmit this application with corrections to restart the
            approval process.
          </Typography>
        )}
      </Alert>

      <Grid container spacing={3}>
        {/* Left Column - Main Details */}
        <Grid item xs={12} md={8}>
          {/* Employee Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PersonIcon />
                Employee Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {leave.user?.first_name} {leave.user?.middle_name}{" "}
                    {leave.user?.sur_name}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{leave.user?.email}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body1">
                    {leave.user?.basicEmployeeData?.department
                      ?.department_name || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Designation
                  </Typography>
                  <Typography variant="body1">
                    {leave.user?.basicEmployeeData?.designation || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Leave Details */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <EventIcon />
                Leave Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Leave Type
                  </Typography>
                  <Chip
                    label={leave.leaveType?.name || "Unknown"}
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    {calculateDays(leave.starting_date, leave.end_date)} days
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(leave.starting_date)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(leave.end_date)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Application Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(leave.created_at)}
                  </Typography>
                </Grid>

                {leave.resubmitted_at && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Last Resubmitted
                    </Typography>
                    <Typography variant="body1" color="primary">
                      {formatDateTime(leave.resubmitted_at)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Comments/Description */}
          {leave.comment && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DescriptionIcon />
                  Comments/Reason
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography variant="body1">{leave.comment}</Typography>
                </Paper>
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          {leave.attachment && leave.attachment.id && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <AttachFileIcon />
                  Attachments
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Paper
                  sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
                >
                  <AttachFileIcon color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">
                      {leave.attachment.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {leave.attachment.file_type || "Document"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadAttachment(leave.attachment)}
                  >
                    Download
                  </Button>
                </Paper>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Approval Info & Actions */}
        <Grid item xs={12} md={4}>
          {/* Approval Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Approval Status
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                {/* Overall status chip */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {getStatusIcon(leave.approval_status)}
                  <Chip
                    label={formatStatus(leave.approval_status)}
                    color={getStatusColor(leave.approval_status)}
                    size="small"
                  />
                </Box>

                {/* Approval Flow Timeline */}
                {(() => {
                  const status = leave.approval_status;
                  const isRejected = status === "rejected";
                  const isDraft = status === "draft";

                  // Step states
                  const step1Done = ["approved by supervisor", "approved by hr", "approved"].includes(status);
                  const step1Current = status === "pending";
                  const step1Rejected = isRejected && !leave.supervisorApprover; // rejected at step 1

                  const step2Done = ["approved by hr", "approved"].includes(status);
                  const step2Current = status === "approved by supervisor";
                  const step2Rejected = isRejected && !!leave.supervisorApprover; // rejected at step 2

                  // Who is the step-1 approver (pending or already approved)
                  const step1Person = leave.supervisorApprover ||
                    (step1Current
                      ? (leave.approver || leave.user?.basicEmployeeData?.supervisor || leave.user?.basicEmployeeData?.department?.head)
                      : null);

                  // Who is the step-2 approver (already approved)
                  const step2Person = step2Done ? leave.approver : null;

                  if (isDraft) return null;

                  const finalDone = status === "approved";
                  const finalRejected = isRejected;

                  // Connector line color: green if step is done, grey otherwise
                  const connectorColor = (stepDone) => stepDone ? "success.main" : "divider";

                  const StepRow = ({ stepNumber, totalSteps, label, person, state }) => {
                    // state: 'done' | 'current' | 'upcoming' | 'rejected'
                    const iconColor = state === "done" ? "success.main"
                      : state === "current" ? "warning.main"
                      : state === "rejected" ? "error.main"
                      : "text.disabled";
                    const Icon = state === "done" ? CheckCircleIcon
                      : state === "rejected" ? RejectedIcon
                      : state === "current" ? PendingIcon
                      : RadioButtonUncheckedIcon;
                    const isLast = stepNumber === totalSteps;

                    return (
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.25 }}>
                          <Icon sx={{ fontSize: 20, color: iconColor }} />
                          {!isLast && (
                            <Box sx={{ width: "2px", height: 28, bgcolor: connectorColor(state === "done"), mt: 0.25 }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, pb: !isLast ? 0.5 : 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Step {stepNumber} — {label}
                          </Typography>
                          {state === "done" && person && (
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                              Approved by <strong>{person.first_name} {person.sur_name}</strong>
                            </Typography>
                          )}
                          {state === "done" && !person && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 0.25, fontWeight: 500 }}>
                              Leave Approved
                            </Typography>
                          )}
                          {state === "current" && (
                            <Box sx={{ mt: 0.5, p: 1, bgcolor: "warning.50", borderRadius: 1, border: "1px solid", borderColor: "warning.200" }}>
                              <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                                {person ? `Awaiting ${person.first_name} ${person.sur_name}` : "Awaiting approval"}
                              </Typography>
                            </Box>
                          )}
                          {state === "rejected" && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 0.25 }}>
                              {leave.approver ? `Rejected by ${leave.approver.first_name} ${leave.approver.sur_name}` : "Rejected"}
                            </Typography>
                          )}
                          {state === "upcoming" && (
                            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.25 }}>Pending</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  };

                  return (
                    <Box>
                      <StepRow
                        stepNumber={1}
                        totalSteps={3}
                        label="Supervisor"
                        person={step1Person}
                        state={step1Rejected ? "rejected" : step1Done ? "done" : step1Current ? "current" : "upcoming"}
                      />
                      <StepRow
                        stepNumber={2}
                        totalSteps={3}
                        label="HR Manager"
                        person={step2Person}
                        state={step2Rejected ? "rejected" : step2Done ? "done" : step2Current ? "current" : "upcoming"}
                      />
                      <StepRow
                        stepNumber={3}
                        totalSteps={3}
                        label="Final Approval"
                        person={null}
                        state={finalRejected ? "rejected" : finalDone ? "done" : "upcoming"}
                      />
                    </Box>
                  );
                })()}
              </Stack>
            </CardContent>
          </Card>

          {/* Quick Actions for Rejected Applications */}
          {canResubmitLeave() && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Application Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="warning" sx={{ mb: 2 }}>
                  This application was rejected. You can resubmit with
                  corrections.
                </Alert>

                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    fullWidth
                    onClick={() => setResubmitDialogOpen(true)}
                  >
                    Resubmit Application
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    fullWidth
                    onClick={handleEdit}
                  >
                    Edit & Resubmit
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions for Approvers */}
          {canApproveLeave() && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Approval Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                  This application requires your approval
                </Alert>

                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApprovedIcon />}
                    fullWidth
                    onClick={() =>
                      navigate(`${ROUTES.LEAVE_APPROVALS}?focus=${leave.id}`)
                    }
                  >
                    Approve Application
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RejectedIcon />}
                    fullWidth
                    onClick={() =>
                      navigate(`${ROUTES.LEAVE_APPROVALS}?focus=${leave.id}`)
                    }
                  >
                    Reject Application
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Linked Attendance Records */}
          {leave.approval_status === 'approved' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttendanceIcon color="primary" />
                    Linked Attendance Records
                  </Typography>
                  <Chip 
                    label={`${attendanceRecords.length} record${attendanceRecords.length !== 1 ? 's' : ''}`}
                    color="info"
                    size="small"
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />

                {loadingAttendance ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : attendanceRecords.length === 0 ? (
                  <Alert severity="info">
                    No attendance records have been generated for this leave yet. 
                    Records are automatically created when the leave is approved.
                  </Alert>
                ) : (
                  <Box>
                    <Grid container spacing={2}>
                      {attendanceRecords.map((record) => (
                        <Grid item xs={12} sm={6} md={4} key={record.id}>
                          <Paper 
                            elevation={1}
                            sx={{ 
                              p: 2, 
                              border: '1px solid',
                              borderColor: record.status === 'on leave' ? 'info.main' : 'divider',
                              borderRadius: 2,
                              '&:hover': {
                                boxShadow: 2,
                                borderColor: 'primary.main'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {new Date(record.date).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </Typography>
                                <Chip
                                  label={record.status}
                                  size="small"
                                  color={
                                    record.status === 'on leave' ? 'info' :
                                    record.status === 'present' ? 'success' :
                                    record.status === 'absent' ? 'error' : 'warning'
                                  }
                                  sx={{ mb: 1 }}
                                />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/attendance/my-records?date=${record.date}`)}
                                sx={{ color: 'primary.main' }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            {record.activity && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {record.activity}
                              </Typography>
                            )}
                            
                            {record.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {record.description}
                              </Typography>
                            )}

                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {record.arrival_time && (
                                <Chip
                                  icon={<CalendarIcon />}
                                  label={`In: ${new Date(record.arrival_time).toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {record.departure_time && (
                                <Chip
                                  icon={<CalendarIcon />}
                                  label={`Out: ${new Date(record.departure_time).toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        startIcon={<AttendanceIcon />}
                        onClick={() => navigate(`/attendance/my-records?month=${new Date(leave.starting_date).getMonth() + 1}&year=${new Date(leave.starting_date).getFullYear()}`)}
                      >
                        View All Attendance Records
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Additional Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1}>
                {leave.leaveType && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Leave Type Details
                    </Typography>
                    <Typography variant="body2">
                      Min: {leave.leaveType.minimum_days} days, Max:{" "}
                      {leave.leaveType.maximum_days} days
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Application ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    #{leave.id}
                  </Typography>
                </Box>

                {((leave.attendanceRecords && leave.attendanceRecords.length > 0) || attendanceRecords.length > 0) && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Linked Attendance Records
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LinkIcon fontSize="small" color="info" />
                      {(leave.attendanceRecords?.length || attendanceRecords.length)} record{(leave.attendanceRecords?.length || attendanceRecords.length) !== 1 ? 's' : ''} generated
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(leave.updated_at)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Leave Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this leave application? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={actionLoading}
          >
            No, Keep Application
          </Button>
          <Button
            onClick={handleCancel}
            color="warning"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? "Cancelling..." : "Yes, Cancel Application"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Leave Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this draft leave application? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? "Deleting..." : "Delete Application"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resubmit Confirmation Dialog */}
      <Dialog
        open={resubmitDialogOpen}
        onClose={() => setResubmitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resubmit Leave Application</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You can resubmit this rejected leave application. Please provide any
            additional comments or corrections below.
          </DialogContentText>

          <TextField
            fullWidth
            label="Additional Comments (Optional)"
            multiline
            rows={4}
            value={resubmitComment}
            onChange={(e) => setResubmitComment(e.target.value)}
            placeholder="Add any clarifications or corrections for the resubmission..."
            sx={{ mt: 2 }}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            Resubmitting will reset the approval status to "Pending" and restart
            the approval process.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResubmitDialogOpen(false);
              setResubmitComment("");
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResubmit}
            color="primary"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? "Resubmitting..." : "Resubmit Application"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveDetailsPage;
